import { toast } from 'react-toastify';

import { getSerializedErrorMessage } from '../../../../utils/errors';
import { finishTreatmentPlanV3Mutation, startTreatmentPlanV3Mutation } from '../../utils';

import type { ActionReducerMapBuilder } from '@reduxjs/toolkit';
import type { IReferral } from '../../../../pages/common';
import type { TreatmentPlanV3PlanAction, TreatmentPlanV3State } from '../../types';
import type {
  CreateTreatmentPlanV3ReferralPayload,
  UpdateTreatmentPlanV3ReferralPayload,
  DeleteTreatmentPlanV3ReferralPayload,
  ReferralMutationAction,
  TreatmentPlanV3ReferralThunks,
} from './types';

export function addTreatmentPlanV3ReferralCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3ReferralThunks,
) {
  addGetTreatmentPlanV3ReferralPageCases(builder, thunks);
  addCreateTreatmentPlanV3ReferralCases(builder, thunks);
  addUpdateTreatmentPlanV3ReferralCases(builder, thunks);
  addDeleteTreatmentPlanV3ReferralCases(builder, thunks);
}

function isForLoadedReferralPage(state: TreatmentPlanV3State, action: TreatmentPlanV3PlanAction) {
  return action.meta.arg.treatmentPlanId === state.referralPageTreatmentPlanId;
}

function markReferralPageChanged(state: TreatmentPlanV3State) {
  if (state.referralPageLoading) {
    state.referralPageChangedSinceRequest = true;
  }
}

function addGetTreatmentPlanV3ReferralPageCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3ReferralThunks,
) {
  const { getTreatmentPlanV3ReferralPage } = thunks;

  builder
    .addCase(getTreatmentPlanV3ReferralPage.pending, (state, action) => {
      // A reload of the same plan keeps the current page; only switching plans discards it.
      if (!isForLoadedReferralPage(state, action)) {
        state.referralPage = null;
      }
      state.referralPageLoading = true;
      state.referralPageError = null;
      state.referralPageRequestId = action.meta.requestId;
      state.referralPageTreatmentPlanId = action.meta.arg.treatmentPlanId;
      state.referralPageChangedSinceRequest = false;
      state.error = null;
    })
    .addCase(getTreatmentPlanV3ReferralPage.fulfilled, (state, action) => {
      if (action.meta.requestId !== state.referralPageRequestId) return;
      state.referralPageLoading = false;
      state.referralPageError = null;
      // A response built before an in-flight save would roll that save back.
      if (!state.referralPageChangedSinceRequest || !state.referralPage) {
        state.referralPage = action.payload;
      }
      state.referralPageChangedSinceRequest = false;
    })
    .addCase(getTreatmentPlanV3ReferralPage.rejected, (state, action) => {
      if (action.meta.requestId !== state.referralPageRequestId) return;
      state.referralPageLoading = false;
      state.referralPageError = action.error;
      state.error = action.error;
    });
}

function addCreateTreatmentPlanV3ReferralCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3ReferralThunks,
) {
  const { createTreatmentPlanV3Referral } = thunks;
  const flag = 'createReferralLoading';

  builder
    .addCase(createTreatmentPlanV3Referral.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      createTreatmentPlanV3Referral.fulfilled,
      (state, action: ReferralMutationAction<IReferral, CreateTreatmentPlanV3ReferralPayload>) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (!isForLoadedReferralPage(state, action) || !state.referralPage) return;
        markReferralPageChanged(state);
        state.referralPage.push(action.payload);
      },
    )
    .addCase(createTreatmentPlanV3Referral.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      if (!isForLoadedReferralPage(state, action)) return;
      state.error = action.error;
    });
}

function addUpdateTreatmentPlanV3ReferralCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3ReferralThunks,
) {
  const { updateTreatmentPlanV3Referral } = thunks;
  const flag = 'updateReferralLoading';

  builder
    .addCase(updateTreatmentPlanV3Referral.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      updateTreatmentPlanV3Referral.fulfilled,
      (state, action: ReferralMutationAction<IReferral, UpdateTreatmentPlanV3ReferralPayload>) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (!isForLoadedReferralPage(state, action) || !state.referralPage) return;
        markReferralPageChanged(state);
        const list = state.referralPage;
        const index = list.findIndex((row) => row.id === action.payload.id);
        if (index !== -1) list[index] = action.payload;
      },
    )
    .addCase(updateTreatmentPlanV3Referral.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      if (!isForLoadedReferralPage(state, action)) return;
      state.error = action.error;
    });
}

function addDeleteTreatmentPlanV3ReferralCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3ReferralThunks,
) {
  const { deleteTreatmentPlanV3Referral } = thunks;
  const flag = 'deleteReferralLoading';

  builder
    .addCase(deleteTreatmentPlanV3Referral.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      deleteTreatmentPlanV3Referral.fulfilled,
      (state, action: ReferralMutationAction<void, DeleteTreatmentPlanV3ReferralPayload>) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (!isForLoadedReferralPage(state, action) || !state.referralPage) return;
        markReferralPageChanged(state);
        state.referralPage = state.referralPage.filter(
          (row) => row.id !== action.meta.arg.referralId,
        );
      },
    )
    .addCase(deleteTreatmentPlanV3Referral.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      if (!isForLoadedReferralPage(state, action)) return;
      state.error = action.error;
    });
}
